import { useId, useMemo, useState, type FormEvent } from "react";
import { formatDate } from "../format.js";
import {
  createStudentExamProject,
  getStaffStudentExamProjects,
  updateStudentExamProject,
  type ExamProjectInput,
  type StudentExamProject,
} from "../examPreparation.js";
import type { StaffStudentGoals, StudentGoal } from "../studentGoals.js";
import type { RepertoireEntry, RepertoireList } from "../repertoire.js";
import { useAsync, type Async } from "../useAsync.js";
import "./ExamProjects.css";

type GoalOption = Pick<StudentGoal, "id" | "title" | "status" | "targetDate">;
type RepertoireOption = Pick<RepertoireEntry, "id" | "title" | "composer" | "status">;

type ProjectDraft = {
  board: string;
  examType: string;
  grade: string;
  instrument: string;
  examDate: string;
  examDateKind: "tentative" | "confirmed";
  requirements: string;
  entryMilestone: string;
  result: string;
  status: "active" | "completed" | "withdrawn";
  familyVisible: boolean;
  familyResultVisible: boolean;
  goalIds: string[];
  repertoireIds: string[];
};

const EMPTY_DRAFT: ProjectDraft = {
  board: "",
  examType: "",
  grade: "",
  instrument: "",
  examDate: "",
  examDateKind: "tentative",
  requirements: "",
  entryMilestone: "",
  result: "",
  status: "active",
  familyVisible: false,
  familyResultVisible: false,
  goalIds: [],
  repertoireIds: [],
};

function draftFor(project?: StudentExamProject): ProjectDraft {
  if (!project) return EMPTY_DRAFT;
  return {
    board: project.board ?? "",
    examType: project.examType ?? "",
    grade: project.grade ?? "",
    instrument: project.instrument ?? "",
    examDate: project.examDate ?? "",
    examDateKind: project.examDateKind,
    requirements: project.requirements ?? "",
    entryMilestone: project.entryMilestone ?? "",
    result: project.result ?? "",
    status: project.status,
    familyVisible: project.familyVisible ?? false,
    familyResultVisible: project.familyResultVisible,
    goalIds: project.linkedGoals.map((goal) => goal.id),
    repertoireIds: project.linkedRepertoire.map((piece) => piece.id),
  };
}

function statusLabel(status: ProjectDraft["status"]): string {
  if (status === "completed") return "Completed";
  if (status === "withdrawn") return "Withdrawn";
  return "In preparation";
}

function goalStatusLabel(status: GoalOption["status"]): string {
  if (status === "abandoned") return "stopped";
  return status;
}

function dateLabel(date: string | null, kind: "tentative" | "confirmed"): string {
  if (!date) return "Date not set";
  return formatDate(date + "T12:00:00Z") + (kind === "confirmed" ? " · confirmed" : " · tentative");
}

function GoalChoices({
  options,
  selected,
  onToggle,
}: {
  options: GoalOption[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <details>
      <summary>Link existing goals ({selected.length} selected)</summary>
      {options.length === 0 ? (
        <p className="small muted">No goals are available to link yet. You can still save this exam plan.</p>
      ) : (
        <fieldset className="stack exam-projects-fieldset">
          <legend className="small muted">Goals and milestones</legend>
          {options.map((goal) => (
            <label className="row exam-projects-choice" key={goal.id}>
              <input
                type="checkbox"
                checked={selected.includes(goal.id)}
                onChange={() => onToggle(goal.id)}
              />
              <span>
                {goal.title}
                <span className="small muted"> · {goalStatusLabel(goal.status)}</span>
                {goal.targetDate && <span className="small muted"> · target {formatDate(goal.targetDate + "T12:00:00Z")}</span>}
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </details>
  );
}

function RepertoireChoices({
  options,
  selected,
  onToggle,
}: {
  options: RepertoireOption[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <details>
      <summary>Link repertoire ({selected.length} selected)</summary>
      {options.length === 0 ? (
        <p className="small muted">No repertoire is available to link yet. You can still save this exam plan.</p>
      ) : (
        <fieldset className="stack exam-projects-fieldset">
          <legend className="small muted">Existing repertoire</legend>
          {options.map((piece) => (
            <label className="row exam-projects-choice" key={piece.id}>
              <input
                type="checkbox"
                checked={selected.includes(piece.id)}
                onChange={() => onToggle(piece.id)}
              />
              <span>
                {piece.composer ? piece.composer + " — " : ""}{piece.title}
                <span className="small muted"> · {piece.status.replaceAll("_", " ")}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </details>
  );
}

function ExamProjectForm({
  studentId,
  project,
  goalOptions,
  repertoireOptions,
  onCancel,
  onSaved,
}: {
  studentId: string;
  project?: StudentExamProject;
  goalOptions: GoalOption[];
  repertoireOptions: RepertoireOption[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(() => draftFor(project));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const goals = useMemo(() => {
    const byId = new Map(goalOptions.map((goal) => [goal.id, goal]));
    for (const linked of project?.linkedGoals ?? []) {
      if (!byId.has(linked.id)) byId.set(linked.id, { ...linked, targetDate: linked.targetDate ?? null });
    }
    return [...byId.values()];
  }, [goalOptions, project?.linkedGoals]);

  const repertoire = useMemo(() => {
    const byId = new Map(repertoireOptions.map((piece) => [piece.id, piece]));
    for (const linked of project?.linkedRepertoire ?? []) {
      if (!byId.has(linked.id)) byId.set(linked.id, linked);
    }
    return [...byId.values()];
  }, [project?.linkedRepertoire, repertoireOptions]);

  function toggle(field: "goalIds" | "repertoireIds", id: string) {
    setDraft((current) => ({
      ...current,
      [field]: current[field].includes(id)
        ? current[field].filter((selected) => selected !== id)
        : [...current[field], id],
    }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setProblem(null);
    const input: ExamProjectInput = {
      board: draft.board || null,
      examType: draft.examType || null,
      grade: draft.grade || null,
      instrument: draft.instrument || null,
      examDate: draft.examDate || null,
      examDateKind: draft.examDateKind,
      requirements: draft.requirements || null,
      entryMilestone: draft.entryMilestone || null,
      familyVisible: draft.familyVisible,
      familyResultVisible: draft.familyVisible && draft.familyResultVisible,
      goalIds: draft.goalIds,
      repertoireIds: draft.repertoireIds,
    };
    if (project) {
      input.status = draft.status;
      if (draft.status === "completed") input.result = draft.result || null;
    }
    try {
      if (project) {
        await updateStudentExamProject(studentId, project.id, {
          ...input,
          revision: project.revision ?? 1,
        });
      } else {
        await createStudentExamProject(studentId, input);
      }
      onSaved();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not save this exam plan.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="panel stack exam-projects-form" onSubmit={save}>
      <div className="spread">
        <strong>{project ? "Edit exam plan" : "New exam plan"}</strong>
        <button type="button" className="quiet small" onClick={onCancel}>Cancel</button>
      </div>
      {problem && <div className="notice small" role="alert">{problem}</div>}
      <div className="row exam-projects-form-fields">
        <label>
          <span className="small muted">Board (optional)</span>
          <input maxLength={160} value={draft.board} onChange={(event) => setDraft((current) => ({ ...current, board: event.target.value }))} />
        </label>
        <label>
          <span className="small muted">Exam type</span>
          <input maxLength={200} value={draft.examType} onChange={(event) => setDraft((current) => ({ ...current, examType: event.target.value }))} />
        </label>
        <label>
          <span className="small muted">Grade (optional)</span>
          <input maxLength={200} value={draft.grade} onChange={(event) => setDraft((current) => ({ ...current, grade: event.target.value }))} />
        </label>
        <label>
          <span className="small muted">Instrument (optional)</span>
          <input maxLength={200} value={draft.instrument} onChange={(event) => setDraft((current) => ({ ...current, instrument: event.target.value }))} />
        </label>
      </div>
      <div className="row exam-projects-form-fields">
        <label>
          <span className="small muted">Exam date (optional)</span>
          <input type="date" value={draft.examDate} onChange={(event) => setDraft((current) => ({ ...current, examDate: event.target.value }))} />
        </label>
        <label>
          <span className="small muted">Date confidence</span>
          <select value={draft.examDateKind} onChange={(event) => setDraft((current) => ({ ...current, examDateKind: event.target.value as ProjectDraft["examDateKind"] }))}>
            <option value="tentative">Tentative</option>
            <option value="confirmed">Confirmed</option>
          </select>
        </label>
        {project && (
          <label>
            <span className="small muted">Plan status</span>
            <select value={draft.status} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as ProjectDraft["status"] }))}>
              <option value="active">In preparation</option>
              <option value="completed">Completed</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </label>
        )}
      </div>
      <label>
        <span className="small muted">Tutor-supplied requirements (optional)</span>
        <textarea rows={3} maxLength={4000} value={draft.requirements} onChange={(event) => setDraft((current) => ({ ...current, requirements: event.target.value }))} />
      </label>
      <label>
        <span className="small muted">Entry milestone (optional)</span>
        <textarea rows={2} maxLength={4000} value={draft.entryMilestone} onChange={(event) => setDraft((current) => ({ ...current, entryMilestone: event.target.value }))} />
      </label>
      <p className="small muted exam-projects-form-note">
        Enter requirements supplied by the tutor. The Hub does not look up syllabus content or score exam readiness.
      </p>
      <GoalChoices options={goals} selected={draft.goalIds} onToggle={(id) => toggle("goalIds", id)} />
      <RepertoireChoices options={repertoire} selected={draft.repertoireIds} onToggle={(id) => toggle("repertoireIds", id)} />
      <label className="row exam-projects-choice">
        <input type="checkbox" checked={draft.familyVisible} onChange={(event) => setDraft((current) => ({
          ...current,
          familyVisible: event.target.checked,
          familyResultVisible: event.target.checked ? current.familyResultVisible : false,
        }))} />
        <span>Share this exam plan and its selected milestones with the student/family</span>
      </label>
      {project && draft.status === "completed" && (
        <>
          <label>
            <span className="small muted">Result (optional)</span>
            <textarea rows={2} maxLength={4000} value={draft.result} onChange={(event) => setDraft((current) => ({ ...current, result: event.target.value }))} />
          </label>
          {draft.familyVisible && (
            <label className="row exam-projects-choice">
              <input type="checkbox" checked={draft.familyResultVisible} onChange={(event) => setDraft((current) => ({ ...current, familyResultVisible: event.target.checked }))} />
              <span>Share this result separately with the student/family</span>
            </label>
          )}
        </>
      )}
      <div className="row exam-projects-actions">
        <button type="submit" disabled={saving}>{saving ? "Saving…" : project ? "Save exam plan" : "Create exam plan"}</button>
        <button type="button" className="quiet" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

function ExamProjectCard({
  studentId,
  project,
  goalOptions,
  repertoireOptions,
  onChanged,
}: {
  studentId: string;
  project: StudentExamProject;
  goalOptions: GoalOption[];
  repertoireOptions: RepertoireOption[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const labelId = useId();
  return (
    <article className="panel stack exam-projects-card">
      <div className="spread exam-projects-card-heading">
        <div>
          <h3 id={labelId} className="exam-projects-title">
            {[project.board, project.examType, project.grade ? "Grade " + project.grade : null, project.instrument]
              .filter(Boolean).join(" · ") || "Exam plan"}
          </h3>
          <p className="small muted exam-projects-meta">
            {dateLabel(project.examDate, project.examDateKind)} · {statusLabel(project.status)}
            {project.familyVisible ? " · shared with family" : " · tutor only"}
          </p>
        </div>
        {project.status === "active" && !editing && (
          <button type="button" className="quiet small" aria-labelledby={labelId} onClick={() => setEditing(true)}>Edit plan</button>
        )}
      </div>
      {project.requirements && <p className="exam-projects-copy">{project.requirements}</p>}
      {project.entryMilestone && <p className="small muted exam-projects-copy">Entry milestone: {project.entryMilestone}</p>}
      {project.linkedGoals.length > 0 && (
        <div className="small">
          <strong>Linked goals</strong>
          <ul>{project.linkedGoals.map((goal) => (
            <li key={goal.id}>{goal.title} <span className="muted">· {goalStatusLabel(goal.status)}</span></li>
          ))}</ul>
        </div>
      )}
      {project.linkedRepertoire.length > 0 && (
        <div className="small">
          <strong>Linked repertoire</strong>
          <ul>{project.linkedRepertoire.map((piece) => (
            <li key={piece.id}>{piece.composer ? piece.composer + " — " : ""}{piece.title} <span className="muted">· {piece.status.replaceAll("_", " ")}</span></li>
          ))}</ul>
        </div>
      )}
      {project.result && (
        <p className="small">Result: {project.result}{project.familyResultVisible ? " · shared with family" : " · private"}</p>
      )}
      {editing && project.status === "active" && (
        <ExamProjectForm
          key={project.id}
          studentId={studentId}
          project={project}
          goalOptions={goalOptions}
          repertoireOptions={repertoireOptions}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
        />
      )}
    </article>
  );
}

export function StudentExamProjects({
  studentId,
  goals,
  repertoire,
  onChanged,
}: {
  studentId: string;
  goals: Async<StaffStudentGoals>;
  repertoire: Async<RepertoireList>;
  onChanged: () => void;
}) {
  const headingId = useId();
  const projects = useAsync(() => getStaffStudentExamProjects(studentId), [studentId]);
  const [creating, setCreating] = useState(false);
  const goalOptions = useMemo(() => {
    const byId = new Map<string, GoalOption>();
    for (const goal of [...(goals.data?.current ?? []), ...(goals.data?.history ?? [])]) byId.set(goal.id, goal);
    return [...byId.values()];
  }, [goals.data]);
  const repertoireOptions = useMemo(() => {
    const byId = new Map<string, RepertoireOption>();
    for (const piece of [...(repertoire.data?.current ?? []), ...(repertoire.data?.completed.items ?? [])]) {
      if (!piece.archivedAt) byId.set(piece.id, piece);
    }
    return [...byId.values()];
  }, [repertoire.data]);

  if (projects.error === "Not found.") return null;

  return (
    <section className="panel stack exam-projects-section" aria-labelledby={headingId}>
      <div className="student-progress-summary-head">
        <div>
          <h3 id={headingId}>Exam preparation{projects.data && <span className="muted"> · {projects.data.length}</span>}</h3>
          <p className="small muted exam-projects-meta">
            Optional tutor-led plans linked to existing goals and repertoire.
          </p>
        </div>
        {!creating && <button type="button" className="student-progress-detail-link" onClick={() => setCreating(true)}>Add exam plan</button>}
      </div>
      {projects.loading && !projects.data && <p className="small muted" role="status">Loading exam plans…</p>}
      {projects.error && <p className="small" role="alert">Exam plans could not be loaded. <button type="button" className="student-progress-detail-link" onClick={projects.reload}>Retry</button></p>}
      {projects.data && projects.data.length === 0 && <p className="small muted">No exam plans yet.</p>}
      {projects.data?.map((project) => (
        <ExamProjectCard
          key={project.id}
          studentId={studentId}
          project={project}
          goalOptions={goalOptions}
          repertoireOptions={repertoireOptions}
          onChanged={() => { projects.reload(); onChanged(); }}
        />
      ))}
      {creating && (
        <ExamProjectForm
          studentId={studentId}
          goalOptions={goalOptions}
          repertoireOptions={repertoireOptions}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); projects.reload(); onChanged(); }}
        />
      )}
    </section>
  );
}
